import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/graphql', '') || "http://localhost:4000";

export type SystemStatusType = "operational" | "maintenance_scheduled" | "maintenance" | "degraded";

export interface SystemStatus {
  id: string;
  status: SystemStatusType;
  message: string | null;
  scheduledAt: Date | null;
  startedAt: Date | null;
  estimatedEnd: Date | null;
  updatedAt: Date;
}

export class SystemStatusStore {
  private rootStore: RootStore;
  private pollInterval: NodeJS.Timeout | null = null;
  private readonly POLL_INTERVAL_FALLBACK_MS = 10000;  // 10 seg cuando SSE no está conectado
  private readonly POLL_INTERVAL_SSE_MS = 180000;      // 180 seg (3 min) cuando SSE está conectado
  private previousStatus: SystemStatusType | null = null;

  systemStatus: SystemStatus | null = null;
  loading: boolean = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  private get currentPollInterval(): number {
    // Si SSE está conectado, polling menos frecuente (SSE envía updates en tiempo real)
    const isSSEConnected = this.rootStore.eventSourceStore?.connectionState === 'connected';
    return isSSEConnected ? this.POLL_INTERVAL_SSE_MS : this.POLL_INTERVAL_FALLBACK_MS;
  }

  get isOperational(): boolean {
    return this.systemStatus?.status === "operational";
  }

  get isMaintenanceScheduled(): boolean {
    return this.systemStatus?.status === "maintenance_scheduled";
  }

  get isInMaintenance(): boolean {
    return this.systemStatus?.status === "maintenance";
  }

  get isDegraded(): boolean {
    return this.systemStatus?.status === "degraded";
  }

  get shouldShowOverlay(): boolean {
    return this.isMaintenanceScheduled || this.isInMaintenance;
  }

  get secondsUntilMaintenance(): number | null {
    if (!this.isMaintenanceScheduled || !this.systemStatus?.scheduledAt) {
      return null;
    }

    const now = Date.now();
    const scheduled = new Date(this.systemStatus.scheduledAt).getTime();
    const diff = Math.max(0, Math.floor((scheduled - now) / 1000));
    return diff;
  }

  async fetchSystemStatus(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const response = await fetch(`${BASE_URL}/api/system-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      runInAction(() => {
        this.systemStatus = {
          ...data,
          scheduledAt: data.scheduledAt ? new Date(data.scheduledAt) : null,
          startedAt: data.startedAt ? new Date(data.startedAt) : null,
          estimatedEnd: data.estimatedEnd ? new Date(data.estimatedEnd) : null,
          updatedAt: new Date(data.updatedAt)
        };
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Unknown error";
        this.loading = false;
      });
      console.error("Error fetching system status:", err);
    }
  }

  startPolling(): void {
    if (this.pollInterval) {
      return;
    }

    this.fetchSystemStatus();
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    // Usar setTimeout recursivo para evaluar intervalo dinámicamente
    this.pollInterval = setTimeout(() => {
      this.fetchSystemStatus();
      this.scheduleNextPoll();
    }, this.currentPollInterval);
  }

  stopPolling(): void {
    if (this.pollInterval) {
      clearTimeout(this.pollInterval);
      this.pollInterval = null;
    }
  }

  handleSSEUpdate(payload: any): void {
    const prevStatus = this.systemStatus?.status || null;

    runInAction(() => {
      this.previousStatus = prevStatus;
      this.systemStatus = {
        ...payload,
        scheduledAt: payload.scheduledAt ? new Date(payload.scheduledAt) : null,
        startedAt: payload.startedAt ? new Date(payload.startedAt) : null,
        estimatedEnd: payload.estimatedEnd ? new Date(payload.estimatedEnd) : null,
        updatedAt: new Date(payload.updatedAt)
      };
    });

    console.log('[SystemStatus] SSE update received:', this.systemStatus);
  }

  handleMaintenanceTransition(): void {
    if (
      this.previousStatus === "maintenance" &&
      this.systemStatus?.status === "operational"
    ) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  dispose(): void {
    this.stopPolling();
  }
}
